import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Category = "films" | "series" | "football" | "ufc";

interface SeoSiloLinksProps {
  category: Category;
  currentSlug?: string;
}

const hubMeta: Record<Category, { href: string; label: string }> = {
  films: { href: "/films", label: "Tous les films en streaming VF" },
  series: { href: "/series", label: "Toutes les séries en streaming VF" },
  football: { href: "/football", label: "Football en direct streaming" },
  ufc: { href: "/ufc", label: "UFC & MMA en direct streaming" },
};

const crossCategories: Record<Category, Category[]> = {
  films: ["series", "football", "ufc"],
  series: ["films", "football", "ufc"],
  football: ["ufc", "films", "series"],
  ufc: ["football", "films", "series"],
};

const globalLinks = [
  { href: "/", label: "Accueil RoiDuStreaming" },
  { href: "/nos-offres", label: "Nos offres IPTV" },
  { href: "/abonnement-iptv", label: "Abonnement IPTV" },
  { href: "/iptv-france", label: "IPTV France" },
  { href: "/iptv-pas-cher", label: "IPTV pas cher" },
  { href: "/abonnement-iptv-3-mois", label: "Abonnement IPTV 3 mois" },
  { href: "/abonnement-iptv-6-mois", label: "Abonnement IPTV 6 mois" },
  { href: "/abonnement-iptv-12-mois", label: "Abonnement IPTV 12 mois" },
  { href: "/avis-clients", label: "Avis clients" },
  { href: "/tutoriels", label: "Tutoriels d'installation IPTV" },
  { href: "/faq", label: "FAQ" },
];

function buildLabel(cat: Category, item: any): string {
  if (cat === "films") return `${item.title} streaming VF`;
  if (cat === "series") return `${item.title} streaming VF`;
  if (cat === "football") return `${item.home_team} vs ${item.away_team} en direct`;
  return `${item.fighter_a} vs ${item.fighter_b} streaming`;
}

function buildHref(cat: Category, slug: string): string {
  return `/${cat}/${slug}`;
}

const SeoSiloLinks = ({ category, currentSlug }: SeoSiloLinksProps) => {
  // Fetch 8 siblings (same category)
  const { data: siblings } = useQuery({
    queryKey: ["silo-siblings", category, currentSlug],
    queryFn: async () => {
      if (category === "films") {
        const { data } = await supabase.from("movies").select("slug, title").eq("published", true).neq("slug", currentSlug || "").order("created_at", { ascending: false }).limit(8);
        return (data || []).map(d => ({ ...d, cat: "films" as Category }));
      }
      if (category === "series") {
        const { data } = await supabase.from("series").select("slug, title").eq("published", true).neq("slug", currentSlug || "").order("created_at", { ascending: false }).limit(8);
        return (data || []).map(d => ({ ...d, cat: "series" as Category }));
      }
      if (category === "football") {
        const { data } = await supabase.from("football_matches").select("slug, home_team, away_team").eq("published", true).neq("slug", currentSlug || "").order("match_date", { ascending: true }).limit(8);
        return (data || []).map(d => ({ ...d, cat: "football" as Category }));
      }
      // ufc
      const { data } = await supabase.from("ufc_fights").select("slug, fighter_a, fighter_b").eq("published", true).neq("slug", currentSlug || "").order("fight_date", { ascending: true }).limit(8);
      return (data || []).map(d => ({ ...d, cat: "ufc" as Category }));
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch 2-3 items per cross-category (total ~6-9 cross links)
  const crossCats = crossCategories[category];
  const { data: crossLinks } = useQuery({
    queryKey: ["silo-cross", category],
    queryFn: async () => {
      const results: { href: string; label: string }[] = [];

      // Always link to cross hub pages first
      for (const c of crossCats) {
        results.push({ href: hubMeta[c].href, label: hubMeta[c].label });
      }

      // Films cross
      const { data: films } = await supabase.from("movies").select("slug, title").eq("published", true).order("created_at", { ascending: false }).limit(2);
      if (films && category !== "films") {
        films.forEach(f => results.push({ href: `/films/${f.slug}`, label: `${f.title} streaming VF` }));
      }

      // Series cross
      const { data: series } = await supabase.from("series").select("slug, title").eq("published", true).order("created_at", { ascending: false }).limit(2);
      if (series && category !== "series") {
        series.forEach(s => results.push({ href: `/series/${s.slug}`, label: `${s.title} streaming VF` }));
      }

      // Football cross
      const { data: matches } = await supabase.from("football_matches").select("slug, home_team, away_team").eq("published", true).order("match_date", { ascending: true }).limit(2);
      if (matches && category !== "football") {
        matches.forEach(m => results.push({ href: `/football/${m.slug}`, label: `${m.home_team} vs ${m.away_team} en direct` }));
      }

      // UFC cross
      const { data: fights } = await supabase.from("ufc_fights").select("slug, fighter_a, fighter_b").eq("published", true).order("fight_date", { ascending: true }).limit(2);
      if (fights && category !== "ufc") {
        fights.forEach(f => results.push({ href: `/ufc/${f.slug}`, label: `${f.fighter_a} vs ${f.fighter_b} streaming` }));
      }

      return results;
    },
    staleTime: 5 * 60 * 1000,
  });

  const hub = hubMeta[category];
  const siblingLinks = (siblings || []).map(s => ({
    href: buildHref(s.cat, s.slug),
    label: buildLabel(s.cat, s),
  }));

  const allLinks = [
    { href: hub.href, label: `← ${hub.label}` },
    ...siblingLinks,
    ...(crossLinks || []),
    ...globalLinks,
  ];

  // Deduplicate
  const seen = new Set<string>();
  const uniqueLinks = allLinks.filter(l => {
    if (seen.has(l.href)) return false;
    seen.add(l.href);
    return true;
  });

  return (
    <nav aria-label="Liens internes SEO" className="py-10 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-lg font-bold text-foreground mb-2 text-center">
          Découvrez aussi sur RoiDuStreaming
        </h2>
        <p className="text-sm text-muted-foreground text-center mb-5">
          Films, séries, football et UFC en streaming HD
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {uniqueLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="px-3 py-1.5 rounded-full text-sm border border-border/50 text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default SeoSiloLinks;
